import { useSyncStatus, type SyncQueue, type SyncStatus } from "@norkron/web-sync-queue";

/**
 * The offline indicator (design §4.3) — requirement 1 ("show when the app is
 * offline"). It renders the mandated conformance markers so it passes
 * `web-sync-queue`'s `WEB_SYNC_QUEUE_DEPTH_HIDDEN_OFFLINE` self-check
 * (`[data-sync-status="offline-queue"]` + `[data-sync-queue-depth]` whenever the
 * queue is offline with depth > 0). Presentation is app-owned (elaborate tokens);
 * the offline LOGIC (composite connectivity + queue depth) is all
 * `web-sync-queue`.
 */

export type SyncMode = "offline" | "syncing" | "degraded" | "idle";

export interface IndicatorState {
  mode: SyncMode;
  /** Unsynced entries in flight or waiting (pending + in_flight). */
  depth: number;
  /** The `[data-sync-status]` marker value, or null when hidden. */
  dataSyncStatus: "offline-queue" | "syncing" | "degraded" | null;
  label: string;
  visible: boolean;
}

/**
 * PURE derivation of the indicator state from the queue status + the SSE stream
 * status. Server-authoritative precedence: OFFLINE (red) wins over syncing;
 * a degraded (reconnecting) stream shows only while otherwise online + idle.
 */
export function deriveIndicator(
  status: SyncStatus,
  streamStatus?: string,
): IndicatorState {
  const depth = status.pending + status.in_flight;
  if (!status.online) {
    return {
      mode: "offline",
      depth,
      dataSyncStatus: "offline-queue",
      label:
        depth > 0
          ? `Offline — ${depth} change${depth === 1 ? "" : "s"} queued`
          : "Offline",
      visible: true,
    };
  }
  if (depth > 0) {
    return {
      mode: "syncing",
      depth,
      dataSyncStatus: "syncing",
      label: `Syncing ${depth}…`,
      visible: true,
    };
  }
  if (streamStatus === "reconnecting") {
    return {
      mode: "degraded",
      depth,
      dataSyncStatus: "degraded",
      label: "Reconnecting…",
      visible: true,
    };
  }
  return { mode: "idle", depth, dataSyncStatus: null, label: "", visible: false };
}

/**
 * PURE presentational view — separated from the hook so it static-renders in
 * unit tests. Renders nothing when idle. Always carries the conformance markers
 * + the pending-count badge + the E2E test id when visible.
 */
export function IndicatorView({ state }: { state: IndicatorState }) {
  if (!state.visible || state.dataSyncStatus === null) return null;
  return (
    <div
      className={`offline-indicator offline-indicator--${state.mode}`}
      role="status"
      aria-live="polite"
      data-testid="offline-indicator"
      data-sync-status={state.dataSyncStatus}
      data-sync-queue-depth={state.depth}
    >
      <span className="offline-indicator__dot" aria-hidden />
      <span className="offline-indicator__label">{state.label}</span>
      {state.depth > 0 ? (
        <span className="offline-indicator__badge" aria-hidden>
          {state.depth}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The live offline indicator — subscribes to the queue's composite connectivity
 * + depth via `useSyncStatus` and renders {@link IndicatorView}. Mount it in
 * `AppShell` (persistent chrome).
 */
export function OfflineIndicator({
  queue,
  streamStatus,
}: {
  queue: SyncQueue;
  streamStatus?: string;
}) {
  const status = useSyncStatus(queue);
  return <IndicatorView state={deriveIndicator(status, streamStatus)} />;
}
